import { Button } from '@fluentui/react-components'
import { Edit24Regular } from '@fluentui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createRestClient } from '@/contexts/QueryProvider'
import { useGetSearchParam } from '@/hooks'
import { ErrorComponent } from '../ErrorComponent'
import { AddApplicationForm } from '../Forms/AddApplicationForm'
import { Loading } from '../Loading'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'
import './AppDetailsPanel.scss'
import { AppEntityTypesPanel } from './AppEntityTypesPanel'
import { MaintainAppAdmins } from './MaintainAppAdmins'
import { AppChatsPanel } from './AppChatsPanel'
import { AppPipelinesPanel } from './AppPipelinesPanel'

export const AppDetailsPanel = () => {
  const queryClient = useQueryClient()
  const appId = useGetSearchParam<number>('appId')
  const { getApplicationsApiGetApplications, getApplicationsApiListApplicationAdmins } = createRestClient()
  const {
    data: application,
    isPending: isApplicationPending,
    isError: isApplicationError,
  } = getApplicationsApiGetApplications.useQuery(undefined, {
    select: (data) => data.find((x) => x.applicationId === appId),
  })

  const invalidateApplications = () => getApplicationsApiGetApplications.invalidateQueries(queryClient)

  const {
    data: currentAppAdmins,
    isPending: isCurrentAppAdminsPending,
    isError: isCurrentAppAdminsError,
  } = getApplicationsApiListApplicationAdmins.useQuery(
    { query: { applicationId: appId } },
    {
      enabled: !!appId,
    }
  )

  const [editAppOpen, setEditAppOpen] = useState(false)
  const [editAdminsOpen, setEditAdminsOpen] = useState(false)

  if (isApplicationPending || isCurrentAppAdminsPending) return <Loading />
  if (isApplicationError || isCurrentAppAdminsError || !application) return <ErrorComponent />

  const { appName, description, status, fileTypes, maxFileSizeMb } = application
  const generalDetails = [
    { label: 'Name', value: appName },
    { label: 'Description', value: description },
    { label: 'Status', value: status },
    { label: 'File formats', value: fileTypes },
    { label: 'Max file size', value: `${maxFileSizeMb}Mb` },
  ]

  return (
    <>
      <div className="flex flex-col gap-y-5 py-4">
        <div className="w-full space-y-4">
          <div className="flex w-full flex-col gap-y-4 rounded bg-white p-6 pt-4">
            <div className="flex flex-row items-center justify-between border-b">
              <h6>General</h6>
              <Button
                appearance="subtle"
                icon={<Edit24Regular className="icon-button" />}
                onClick={() => setEditAppOpen(true)}
              />
            </div>
            <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
              {generalDetails.map(({ label, value }) => (
                <div className="flex flex-col whitespace-nowrap" key={label}>
                  <span className="text-sm font-light">{label}:</span>
                  <span className="text-base">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Application administrators */}
          <div className="flex w-full flex-col gap-y-4 rounded py-5">
            <div className="flex flex-row justify-between gap-2 border-b">
              <h6>Application administrators</h6>
              <Button
                appearance="subtle"
                icon={<Edit24Regular className="icon-button" />}
                onClick={() => {
                  setEditAdminsOpen(true)
                }}
              />
            </div>
            <div className="flex flex-row flex-wrap gap-x-2 whitespace-nowrap text-base">
              {!currentAppAdmins.length && <span className="font-light">(None assigned)</span>}
              {currentAppAdmins.map((a, i) => (
                <span
                  key={`${a.givenName}-${a.familyName}`}
                >{`${a.givenName} ${a.familyName}${i < currentAppAdmins.length - 1 ? ',' : ''}`}</span>
              ))}
            </div>
          </div>
        </div>
        {appId && <AppEntityTypesPanel applicationId={appId} />}
        {appId && <AppChatsPanel applicationId={appId} />}
        {appId && <AppPipelinesPanel applicationId={appId} />}
      </div>

      <OverlayDrawerComponent
        open={editAppOpen}
        setOpen={setEditAppOpen}
        title="Edit application details"
        content={
          <AddApplicationForm
            onSubmit={() => {
              setEditAppOpen(false)
              invalidateApplications()
            }}
            selectedApp={application}
            edit
          />
        }
      />

      <OverlayDrawerComponent
        key="editaadmins"
        size="medium"
        open={editAdminsOpen}
        setOpen={setEditAdminsOpen}
        title="Maintain app admins"
        content={
          <MaintainAppAdmins
            selectedApp={application}
            onSubmit={() => {
              setEditAdminsOpen(false)
            }}
          />
        }
      />
    </>
  )
}
